// Transport DTOs (LC_*) and internal models for Shift domain
// Backend returns LC_PagedResult directly after envelope unwrapping
import type { LC_PagedResult } from '@/api/shared/types';


export type ShiftRequestStatus = 10 | 20 | 30 | 40;
export type OrderDirection = 'ASC' | 'DESC';

// Status constants matching backend
export const SHIFT_REQUEST_STATUS = {
  PENDING: 10,
  APPROVED: 20,
  REJECTED: 30,
  CANCELED: 40,
} as const;

// Status labels for UI
export const SHIFT_REQUEST_STATUS_LABELS: Record<ShiftRequestStatus, string> = {
  10: 'Pendiente',
  20: 'Aprobada',
  30: 'Rechazada',
  40: 'Cancelada',
};

// Submit shift change request DTOs
export interface LC_SubmitShiftChangeRequestIn {
  companyId?: number;           // Optional (from JWT)
  requestingEmployeeId?: number; // Optional (from JWT)
  userId?: string;               // Optional (from JWT)
  requesterComment?: string;     // Optional (max: 500 chars)
  dateFrom: string;              // REQUIRED (ISO date-time: YYYY-MM-DDTHH:mm:ss)
  dateTo: string;                // REQUIRED (ISO date-time: YYYY-MM-DDTHH:mm:ss)
  requestId?: number;            // Optional (output after creation)
}

export interface LC_SubmitShiftChangeRequestOut {
  success: boolean;
  requestId?: number;
  message?: string;
}

// Get shift requests DTOs
export interface LC_GetShiftRequestsIn {
  employeeId: number;     // From path parameter
  status?: ShiftRequestStatus;  // Optional filter: 10, 20, 30, 40
  dateFrom?: string;      // Optional filter (ISO date-time)
  dateTo?: string;        // Optional filter (ISO date-time)
  page?: number;          // Default: 1
  pageSize?: number;      // Default: 10, max: 1000
  searchText?: string;    // Optional search text
  orderBy?: string;       // Optional sort field
  orderDirection?: OrderDirection; // Optional sort direction
}

// Response from backend (LC_EmployeeShiftRequestItem from api.json)
export interface LC_ShiftRequestOut {
  id: number;                          // int64 - ID de la solicitud
  employeeId: number;                  // int32 - ID del empleado
  employeeName: string | null;         // Nombre del empleado (nullable)
  currentScheduleId: number | null;    // int64 - ID del horario actual (nullable)
  dateFrom: string;                    // ISO date-time - Fecha de inicio del cambio
  dateTo: string | null;               // ISO date-time - Fecha de fin del cambio (nullable)
  requesterComment: string | null;     // Comentario del empleado (nullable)
  status: ShiftRequestStatus;          // int32 - Estado de la solicitud
  statusDescription: string | null;    // Descripción del estado (nullable)
  approvalComment: string | null;      // Comentario de aprobación (nullable)
  createdBy: string | null;            // Usuario que creó la solicitud (nullable)
  createdDate: string;                 // ISO date-time - Fecha de creación
  approvedBy: string | null;           // Usuario aprobador (nullable)
  approvalDate: string | null;         // ISO date-time - Fecha de aprobación (nullable)
  reviewerName: string | null;         // Nombre del aprobador (nullable hasta que se revisa)
}

export type LC_GetShiftRequestsOut =
  LC_PagedResult<LC_ShiftRequestOut>;

// Internal UI models
export interface ShiftRequest {
  id: number;
  requestDate: string;    // dd/MM/yyyy
  comments: string;
  dateFrom: string;       // dd/MM/yyyy
  dateTo: string;         // dd/MM/yyyy
  status: string;         // "Pendiente" | "Aprobada" | "Rechazada" | "Cancelada"
  statusCode: ShiftRequestStatus;
  approverComment: string;   // '' cuando aún no ha sido revisada
  reviewDate: string;        // dd/MM/yyyy ('' cuando aún no ha sido revisada)
  reviewerName: string;      // '' cuando aún no ha sido revisada
}

export interface ShiftRequestPage {
  data: ShiftRequest[];
  page: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
}

// Form model for creating new request
export interface ShiftRequestFormData {
  dateFrom: string;       // YYYY-MM-DD
  dateTo: string;         // YYYY-MM-DD
  requesterComment?: string;
}

// ── Export the employee's own listing (ASIS-5555) — Excel file download ─────
// Endpoint: GET /v1/employees/{employeeId}/shift/change-requests/file
// Applies the SAME filters as the listing; companyId is resolved from the JWT on the
// backend and the employee can only export their own requests. Returns a binary .xlsx
// with the filename in the Content-Disposition header.
export interface LC_ExportEmployeeShiftRequestsIn {
  status?: ShiftRequestStatus;  // Optional filter: 10, 20, 30, 40
  dateFrom?: string;            // Optional filter (yyyy/mm/dd)
  dateTo?: string;              // Optional filter (yyyy/mm/dd)
  searchText?: string;          // Optional search text
}

// ── Company-scoped admin listing (ASIS-5230) ───────────────────────────────
// Endpoint: GET /v1/shift/change-requests
// Profiled by access level on the backend; companyId is resolved from the JWT
// (never sent by the client).

export interface LC_GetCompanyShiftRequestsIn {
  // companyId is resolved from the JWT on the backend — never sent by the client.
  status?: ShiftRequestStatus;  // Optional filter: 10, 20, 30, 40
  dateFrom?: string;            // Optional filter (yyyy/mm/dd)
  dateTo?: string;              // Optional filter (yyyy/mm/dd)
  employeeIds?: string;         // Optional CSV of employee IDs (empty = "Todos")
  searchText?: string;          // Optional search text
  orderBy?: string;             // Optional sort field
  orderDirection?: OrderDirection; // Optional sort direction
  page?: number;                // Default: 1
  pageSize?: number;            // Default: 10
}

// Response item (LC_CompanyShiftRequestItem from api.json) — exact backend shape
export interface LC_CompanyShiftRequestItem {
  id: number;                       // int64 - ID de la solicitud
  employeeId: number;               // int32 - ID del empleado
  employeeRut: string;              // RUT del empleado
  employeeName: string;             // "ApellidoPaterno ApellidoMaterno Nombre"
  requestDate: string;              // ISO date-time - Fecha de la solicitud
  dateFrom: string;                 // ISO date-time - Fecha desde
  dateTo: string | null;            // ISO date-time - Fecha hasta (nullable)
  requesterComment: string;         // Comentario del solicitante
  status: number;                   // int32 - Estado de la solicitud
  statusDescription: string;        // Descripción del estado
  reviewDate: string | null;        // ISO date-time - Fecha de revisión (nullable)
  reviewerName: string | null;      // Nombre del revisor (nullable)
  approvalComment: string | null;   // Comentario de aprobación (nullable)
}

// Backend wraps the page under `requests` (LC_PagedResult); the service unwraps it.
export type LC_GetCompanyShiftRequestsOut =
  LC_PagedResult<LC_CompanyShiftRequestItem>;

// ── KPI status counters (ASIS-5237) — dedicated endpoint ────────────────────
// Endpoint: GET /v1/shift/change-requests/counters
// Applies the SAME filters as the listing (status incluido); companyId is resolved
// from the JWT on the backend (never sent by the client).
export interface LC_GetCompanyShiftRequestCountersIn {
  status?: ShiftRequestStatus;  // Optional filter: 10, 20, 30, 40
  dateFrom?: string;            // Optional filter (yyyy/mm/dd)
  dateTo?: string;              // Optional filter (yyyy/mm/dd)
  employeeIds?: string;         // Optional CSV of employee IDs (empty = "Todos")
  searchText?: string;          // Optional search text
}

// Backend output for the counters endpoint.
export interface LC_GetCompanyShiftRequestCountersOut {
  pending: number;
  approved: number;
  rejected: number;
}

// ── Export company-scoped listing (ASIS-5238) — Excel file download ─────────
// Endpoint: GET /v1/shift/change-requests/file
// Applies the SAME filters as the listing; companyId is resolved from the JWT
// on the backend (never sent by the client). Returns a binary .xlsx with the
// filename in the Content-Disposition header.
export interface LC_ExportCompanyShiftRequestsIn {
  status?: number;       // Optional filter: 10, 20, 30, 40
  dateFrom?: string;     // Optional filter (yyyy/mm/dd)
  dateTo?: string;       // Optional filter (yyyy/mm/dd)
  employeeIds?: string;  // Optional CSV of employee IDs (empty = "Todos")
  searchText?: string;   // Optional search text
}

// ── Approve a shift change request (ASIS-5233) ──────────────────────────────
// Endpoint: PUT /v1/shift/change-requests/{requestId}/approval
// Only the approver comment is sent; companyId, approver and request id are
// resolved on the backend (JWT + route). The response always uses HTTP 200 with
// a discriminator: 'approved' (success) or 'alreadyResolved' (concurrency).

export interface LC_ApproveShiftChangeRequestIn {
  approverComment: string; // REQUIRED (max 500 chars)
}

// Real current state of an already-resolved request (concurrency case), used to
// switch the detail panel to read-only in place.
export interface LC_ShiftRequestResolvedState {
  statusCode: number;
  statusDescription: string;
  reviewerName: string;
  reviewDate: string | null;   // ISO date-time
  approverComment: string;
}

export interface LC_ApproveShiftChangeRequestOut {
  outcome: 'approved' | 'alreadyResolved';
  shiftChangeRequestId: number;
  newStatus: number;
  message: string;
  reviewDate: string | null;   // ISO date-time
  currentState: LC_ShiftRequestResolvedState | null;
}

// ── Resolve a shift change request (ASIS-5234) — unified endpoint ───────────
// Endpoint: PUT /v1/shift/change-requests/{requestId}/resolution
// Single endpoint that approves OR rejects based on the `decision` discriminator.
// Only decision + approver comment are sent; companyId, approver and request id
// are resolved on the backend (JWT + route). The response always uses HTTP 200
// with an `outcome` discriminator: 'approved' | 'rejected' (success) or
// 'alreadyResolved' (concurrency, carries the real currentState).

export type ShiftRequestDecision = 'approved' | 'rejected';

export interface LC_ResolveShiftChangeRequestIn {
  decision: ShiftRequestDecision; // REQUIRED ('approved' | 'rejected')
  approverComment: string;        // REQUIRED (max 500 chars)
}

export interface LC_ResolveShiftChangeRequestOut {
  outcome: 'approved' | 'rejected' | 'alreadyResolved';
  shiftChangeRequestId: number;
  newStatus: number;
  message: string;
  reviewDate: string | null;   // ISO date-time
  currentState: LC_ShiftRequestResolvedState | null;
}

// Decision discriminator constants (mirror backend ShiftRequestDecisions).
export const SHIFT_REQUEST_DECISION = {
  APPROVED: 'approved',
  REJECTED: 'rejected',
} as const;

// Outcome discriminator constants (mirror backend ShiftRequestApprovalOutcomes).
export const SHIFT_APPROVAL_OUTCOME = {
  APPROVED: 'approved',
  REJECTED: 'rejected',
  ALREADY_RESOLVED: 'alreadyResolved',
} as const;

// Max length for shift request comments (mirrors backend MAX_COMMENT_LENGTH).
export const SHIFT_COMMENT_MAX_LENGTH = 500;

// -- Company Shift Catalog (timTurno) - ASIS-5035 --------------------------
export interface LC_CompanyShiftItem {
  id: number;
  shiftCode: string | null;
  shiftName: string | null;
  workingDayType: string | null;
  workingDay: string | null;
  createdAt: string;
  status: string | null;
  // ASIS-5045: true when the shift is assigned (current or historical) — governs read-only mode.
  hasAssignments: boolean;
  // ASIS-5044/5054: vigencia de la resolución DT (jornada excepcional).
  isExceptionalWorkingDay: boolean | null;
  dtResolutionExpired: boolean;
  dtResolutionExpiryDate: string | null;
}

// Backend hoists LC_PagedResult via TryExtractPaged; apiClient returns { data, pagination }
// directly (same pattern as LC_GetCompanyShiftRequestsOut).
export type LC_GetCompanyShiftsListOut = LC_PagedResult<LC_CompanyShiftItem>;

export interface CompanyShiftCatalogItem {
  id: number;
  shiftCode: string;
  shiftName: string;
  workingDayType: string;
  workingDay: string;
  createdAt: string;
  status: string;
  hasAssignments: boolean;
  isExceptionalWorkingDay: boolean;
  dtResolutionExpired: boolean;
  dtResolutionExpiryDate: string | null;
}

export interface CompanyShiftCatalogListResult {
  items: CompanyShiftCatalogItem[];
  totalRecords: number;
}

// ── Export company shift catalog (ASIS-5047) — Excel file download ─────────
// Endpoint: GET /v1/attendance/shifts/file
// Applies only the advanced filters of the catalog listing — the free-text search
// is intentionally ignored. companyId is resolved from the JWT on the backend
// (never sent by the client). Returns a binary .xlsx with the filename in the
// Content-Disposition header.
export interface LC_ExportCompanyShiftsIn {
  workingDayType?: string;
  workingDayId?: number;
  status?: string;
}

// -- Schedule catalog for shift assignment (caHorario) - ASIS-5052 ---------
export interface LC_ScheduleItemOut {
  scheduleId: number;
  name: string;
  isTwoDaySchedule: boolean;
  startTime: string;
  endTime: string;
  breakMinutes: number;
  hasBreakMarkings: boolean;
  timeZoneId: number;
  timeZoneDescription: string;
}

export type LC_GetSchedulesListOut = LC_PagedResult<LC_ScheduleItemOut>;

export interface ScheduleOption {
  id: number;
  name: string;
  startTime: string;
  endTime: string;
  breakMinutes: number;
}

export interface ScheduleOptionListResult {
  items: ScheduleOption[];
  totalRecords: number;
}

// -- Create Company Shift (timTurno) - ASIS-5037 ---------------------------
export interface LC_ShiftDayDetailIn {
  daySequence: number;
  scheduleId: number | null;
}

export interface LC_CreateCompanyShiftIn {
  workingDayTypeId: number;
  workingDayId: number;
  shiftCode: string;
  shiftName: string;
  weeksCount: number;
  isExceptionalWorkingDay?: boolean;
  dtAuthorizationNumber?: string;
  dtAuthorizationDate?: string; // yyyy/mm/dd
  staticStartDate?: string; // yyyy/mm/dd
  details: LC_ShiftDayDetailIn[];
}

export interface LC_CreateCompanyShiftOut {
  id: number;
  message: string;
}

// -- Get/Update Company Shift detail + status toggle (ASIS-5045) ----------
export interface LC_GetCompanyShiftDetailOut {
  id: number;
  shiftCode: string | null;
  shiftName: string | null;
  workingDayTypeId: number;
  workingDayId: number;
  weeksCount: number;
  isExceptionalWorkingDay?: boolean;
  dtAuthorizationNumber?: string;
  dtAuthorizationDate?: string; // yyyy/mm/dd
  staticStartDate?: string; // yyyy/mm/dd
  isEnabled: boolean;
  // true = turno bloqueado (asignado, vigente o histórico) → gobierna modo solo lectura
  hasAssignments: boolean;
  details: LC_ShiftDayDetailIn[];
}

export interface LC_UpdateCompanyShiftIn {
  // Sin shiftCode — no editable
  workingDayTypeId: number;
  workingDayId: number;
  shiftName: string;
  weeksCount: number;
  isExceptionalWorkingDay?: boolean;
  dtAuthorizationNumber?: string;
  dtAuthorizationDate?: string; // yyyy/mm/dd
  staticStartDate?: string; // yyyy/mm/dd
  details: LC_ShiftDayDetailIn[];
}

export interface LC_UpdateCompanyShiftOut {
  message: string;
  warnings?: string[];
}

export interface LC_ToggleCompanyShiftStatusIn {
  isEnabled: boolean;
}

export interface LC_ToggleCompanyShiftStatusOut {
  message: string;
}

// -- Delete Company Shift (timTurno) - ASIS-5046 ---------------------------
export interface LC_DeleteCompanyShiftOut {
  id: number;
  message: string;
}

// Internal UI model for the shift detail (Edit page) — same shape as the DTO
// but with non-nullable strings/dates already defaulted for direct form binding.
export interface CompanyShiftDetail {
  id: number;
  shiftCode: string;
  shiftName: string;
  workingDayTypeId: number;
  workingDayId: number;
  weeksCount: number;
  isExceptionalWorkingDay: boolean;
  dtAuthorizationNumber: string;
  dtAuthorizationDate: string; // yyyy/mm/dd
  staticStartDate: string; // yyyy/mm/dd
  isEnabled: boolean;
  hasAssignments: boolean;
  details: LC_ShiftDayDetailIn[];
}

// Internal UI model for the company-scoped admin listing
export interface CompanyShiftRequest {
  id: number;
  employeeId: number;
  employeeRut: string;        // formatted RUT (12345678-9)
  employeeName: string;
  requestDate: string;        // dd/MM/yyyy
  dateFrom: string;           // dd/MM/yyyy
  dateTo: string;             // dd/MM/yyyy ('' when null)
  requesterComment: string;
  status: string;             // status label
  statusCode: number;
  reviewDate: string;         // dd/MM/yyyy ('' when null)
  reviewerName: string;       // '' when null
  approvalComment: string;    // '' when null
}

// UI model for the KPI status counters shown above the listing.
export interface ShiftRequestCounters {
  pending: number;
  approved: number;
  rejected: number;
}

export interface CompanyShiftRequestPage {
  data: CompanyShiftRequest[];
  page: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
}

// ── Bulk approval (ASIS-5235) ────────────────────────────────────────────────
export interface LC_BulkApproveShiftRequestsIn {
  requestIds: string;
  approverComment: string;
}

export interface LC_BulkApproveShiftRequestItemOut {
  shiftChangeRequestId: number;
  outcome: 'approved' | 'alreadyResolved';
  newStatus: number;
  reviewDate: string | null;
  currentState: LC_ShiftRequestResolvedState | null;
}

export interface LC_BulkApproveShiftRequestsOut {
  items: LC_BulkApproveShiftRequestItemOut[];
  totalApproved: number;
  totalAlreadyResolved: number;
}

// ── Bulk rejection (ASIS-5236) ────────────────────────────────────────────────
export interface LC_BulkRejectShiftRequestsIn {
  requestIds: string;
  approverComment: string;
}

export interface LC_BulkRejectShiftRequestItemOut {
  shiftChangeRequestId: number;
  outcome: 'rejected' | 'alreadyResolved';
  newStatus: number;
  reviewDate: string | null;
  currentState: LC_ShiftRequestResolvedState | null;
}

export interface LC_BulkRejectShiftRequestsOut {
  items: LC_BulkRejectShiftRequestItemOut[];
  totalRejected: number;
  totalAlreadyResolved: number;
}
